import { createApi } from "@reduxjs/toolkit/query/react";

import { dynamicBaseQuery } from "@/api/DynamicBaseQuery";
import type {
  TCreateRegularizationResponse,
  TListRegularizationRequest,
  TListRegularizationResponse,
  IProcessRegularizationRequest,
  TProcessRegularizationResponse,
  IUpdateRegularizationRequest,
  TUpdateRegularizationResponse,
  IListRegularizationRequestById,
  TListRegularizationByIdResponse,
  TRegularizationStatsRequest,
  TRegularizationStatsResponse,
} from "@/types/(my-space)/attendanceRegularization.interface";
import type { TPendingApprovalsResponse } from "@/types/(company)/timeAttendance.interface";
import { COMPANY_ATTENDANCE_REGULARIZATION } from "@/constants/apiEndpoints";
import { companyTimeAttendanceApi } from "@/services/(company)/api.time-attendance";

// A regularization write (create / approve-reject / edit) changes the company
// attendance summary + drawer, which live in a different RTK slice
// (`invalidatesTags` can't cross slices). Cross-invalidate the shared summary
// tag (the drawer query provides it too) so approving/rejecting from the
// pending-approvals or regularization pages refetches the attendance views.
const invalidateCompanyAttendanceSummary = async (
  _arg: unknown,
  {
    dispatch,
    queryFulfilled,
  }: {
    dispatch: (action: unknown) => unknown;
    queryFulfilled: Promise<unknown>;
  },
) => {
  try {
    await queryFulfilled;
    dispatch(
      companyTimeAttendanceApi.util.invalidateTags([
        "getCompanyEmployeeTimeAttendanceSummary",
      ]),
    );
  } catch {
    // request failed — nothing to invalidate
  }
};

export const companyAttendanceRegularizationApi = createApi({
  reducerPath: "companyAttendanceRegularizationApi",
  baseQuery: dynamicBaseQuery,
  tagTypes: [
    "getPendingApprovals",
    "getCompanyRegularizationRequests",
    "getCompanyRegularizationStats",
  ],
  endpoints: (builder) => ({
    getPendingApprovals: builder.query<
      TPendingApprovalsResponse,
      { limit?: number }
    >({
      query: ({ limit }) => ({
        url: "/company/attendance-regularization/pending-approvals",
        method: "GET",
        params: { limit },
      }),
      providesTags: ["getPendingApprovals"],
    }),
    getCompanyRegularization: builder.query<
      TListRegularizationResponse,
      TListRegularizationRequest
    >({
      query: (params) => {
        return {
          url: "/company/attendance-regularization/paginated",
          method: "GET",
          params: {
            status: "pending",
            ...params,
          },
        };
      },
      providesTags: ["getCompanyRegularizationRequests"],
    }),
    getCompanyRegularizationStats: builder.query<
      TRegularizationStatsResponse,
      TRegularizationStatsRequest
    >({
      query: (params) => ({
        url: "/company/attendance-regularization/stats",
        method: "GET",
        params,
      }),
      providesTags: ["getCompanyRegularizationStats"],
    }),
    getCompanyRegularizationById: builder.query<
      TListRegularizationByIdResponse,
      IListRegularizationRequestById
    >({
      query: (body) => {
        return {
          url: "/company/attendance-regularization/" + body.id,
          method: "GET",
        };
      },
      providesTags: ["getCompanyRegularizationRequests"],
    }),
    processRegularizationRequest: builder.mutation<
      TProcessRegularizationResponse,
      IProcessRegularizationRequest
    >({
      query: (body) => {
        return {
          url: `/company/attendance-regularization/${body.id}/process`,
          method: "PUT",
          body: body.data,
        };
      },
      invalidatesTags: [
        "getCompanyRegularizationRequests",
        "getPendingApprovals",
        "getCompanyRegularizationStats",
      ],
      onQueryStarted: invalidateCompanyAttendanceSummary,
    }),
    createCompanyRegularizationRequest: builder.mutation<
      TCreateRegularizationResponse,
      FormData
    >({
      query: (body) => {
        return {
          url: "/company/attendance-regularization",
          method: "POST",
          body,
        };
      },
      invalidatesTags: [
        "getCompanyRegularizationRequests",
        "getPendingApprovals",
        "getCompanyRegularizationStats",
      ],
      onQueryStarted: invalidateCompanyAttendanceSummary,
    }),
    updateCompanyRegularizationRequest: builder.mutation<
      TUpdateRegularizationResponse,
      IUpdateRegularizationRequest
    >({
      query: (body) => {
        return {
          url: `${COMPANY_ATTENDANCE_REGULARIZATION}/${body.id}`,
          method: "PUT",
          body: body.data,
        };
      },
      invalidatesTags: [
        "getCompanyRegularizationRequests",
        "getPendingApprovals",
        "getCompanyRegularizationStats",
      ],
      onQueryStarted: invalidateCompanyAttendanceSummary,
    }),
  }),
});

export const {
  useGetPendingApprovalsQuery,
  useGetCompanyRegularizationQuery,
  useGetCompanyRegularizationStatsQuery,
  useGetCompanyRegularizationByIdQuery,
  useProcessRegularizationRequestMutation,
  useCreateCompanyRegularizationRequestMutation,
  useUpdateCompanyRegularizationRequestMutation,
} = companyAttendanceRegularizationApi;
